export interface PanelInfo {
  panelId: string
  panelType: string
  bounds: {
    x: number
    y: number
    width: number
    height: number
  }
}

export interface LabeledElement {
  label: string
  panelType: string | null
  bounds: {
    x: number
    y: number
    width: number
    height: number
  }
}

/**
 * Queries the DOM for all visible workspace panels and their positions.
 * Panels must have `data-panel-id` and `data-panel-type` attributes
 * on their wrapper element.
 */
export function collectPanelLayout(): PanelInfo[] {
  const elements = document.querySelectorAll<HTMLElement>('[data-panel-id][data-panel-type]')
  const panels: PanelInfo[] = []

  elements.forEach((el) => {
    const panelId = el.getAttribute('data-panel-id')
    const panelType = el.getAttribute('data-panel-type')
    if (!panelId || !panelType) return

    // Walk up to the mosaic-window container so bounds include
    // the panel header toolbar (30px), not just the content area.
    // Try multiple selectors for robustness.
    const mosaicWindow =
      el.closest('.mosaic-window') ??
      el.closest('[class*="mosaic-window"]') ??
      el.parentElement?.closest('.mosaic-window-body')?.parentElement

    const rect = mosaicWindow ? mosaicWindow.getBoundingClientRect() : el.getBoundingClientRect()

    // Log for debugging
    if (!mosaicWindow) {
      console.warn('[companion] Could not find mosaic-window parent for panel:', panelId, panelType)
    }

    panels.push({
      panelId,
      panelType,
      bounds: {
        x: Math.round(rect.left),
        y: Math.round(rect.top),
        width: Math.round(rect.width),
        height: Math.round(rect.height),
      },
    })
  })

  return panels
}

/**
 * Queries the DOM for all elements with `data-companion-label` attributes.
 * These are specific interactive elements (buttons, inputs, etc.) that
 * the app has explicitly marked for the companion to know about.
 *
 * Usage in your components:
 *   <button data-companion-label="filters-button">Filters</button>
 *   <input data-companion-label="search-input" />
 */
export function collectLabeledElements(panels: PanelInfo[]): LabeledElement[] {
  const elements = document.querySelectorAll<HTMLElement>('[data-companion-label]')
  const labeled: LabeledElement[] = []

  elements.forEach((el) => {
    const label = el.getAttribute('data-companion-label')
    if (!label) return

    const rect = el.getBoundingClientRect()

    // Find which panel this element belongs to
    const cx = rect.left + rect.width / 2
    const cy = rect.top + rect.height / 2
    let panelType: string | null = null
    for (const panel of panels) {
      const { x, y, width, height } = panel.bounds
      if (cx >= x && cx <= x + width && cy >= y && cy <= y + height) {
        panelType = panel.panelType
        break
      }
    }

    labeled.push({
      label,
      panelType,
      bounds: {
        x: Math.round(rect.left),
        y: Math.round(rect.top),
        width: Math.round(rect.width),
        height: Math.round(rect.height),
      },
    })
  })

  return labeled
}

/** Maximum number of elements included in describeLabeledElements output. */
const LABELED_ELEMENTS_CAP = 40

/**
 * Produces a text block listing labeled interactive elements for the system prompt.
 *
 * Format:
 *   ## interactive elements
 *   - <label> (in <panelType> panel)   ← panelType present
 *   - <label>                           ← no panel attribution (global element)
 *
 * Capped at 40 entries in document order.
 * Returns an empty string when the array is empty.
 */
export function describeLabeledElements(labeled: LabeledElement[]): string {
  if (labeled.length === 0) return ''

  const capped = labeled.slice(0, LABELED_ELEMENTS_CAP)
  const lines = capped.map((el) =>
    el.panelType ? `- ${el.label} (in ${el.panelType} panel)` : `- ${el.label}`,
  )

  return `## interactive elements\n${lines.join('\n')}`
}

/**
 * Finds which panel the mouse cursor is currently over.
 * Returns the panel info or null if the cursor isn't over any panel.
 */
export function findFocusedPanel(
  panels: PanelInfo[],
  mouseX: number,
  mouseY: number,
): PanelInfo | null {
  for (const panel of panels) {
    const { x, y, width, height } = panel.bounds
    if (mouseX >= x && mouseX <= x + width && mouseY >= y && mouseY <= y + height) {
      return panel
    }
  }
  return null
}

/**
 * Finds the last-active panel by checking for the workspace's active panel indicator.
 * Falls back to DOM query for the panel with the active ring style.
 */
export function findActivePanel(panels: PanelInfo[]): PanelInfo | null {
  // The active panel has 'ring-1 ring-primary' class via PanelWrapper
  const activeEl = document.querySelector<HTMLElement>('[data-panel-id].ring-1')
  if (!activeEl) return null

  const activeId = activeEl.getAttribute('data-panel-id')
  return panels.find((p) => p.panelId === activeId) ?? null
}

/**
 * Panel-type synonyms for question-text matching (EN + ES). Keys are the
 * `data-panel-type` values; anything not listed falls back to a literal
 * match on the type name itself (hyphens read as spaces).
 */
const PANEL_TEXT_SYNONYMS: Record<string, RegExp> = {
  chart: /\b(charts?|candles?|gr[aá]fic\w*)\b/i,
  watchlist: /\b(watch\s?lists?)\b/i,
  scanner: /\b(scanners?|screeners?|esc[aá]ner\w*)\b/i,
  news: /\b(news|headlines?|noticias)\b/i,
  'key-stats': /\b(key\s?stats?|fundamentals?|estad[ií]sticas)\b/i,
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * When the user's message names a panel ("change the layout of the chart"),
 * that panel is the target — regardless of where the mouse happens to be.
 * Born from the 2026-08-25 miss: a chart question got the watchlist as
 * target (mouse focus), the chart's elements were collapsed away, and the
 * model dropped the POINT tag entirely. Returns the earliest-mentioned open
 * panel type, or null when no open panel is named.
 */
export function inferPanelTypeFromText(text: string, openPanelTypes: string[]): string | null {
  let best: { type: string; index: number } | null = null
  for (const type of openPanelTypes) {
    const re =
      PANEL_TEXT_SYNONYMS[type] ?? new RegExp(`\\b${escapeRe(type.replace(/-/g, ' '))}\\b`, 'i')
    const match = re.exec(text)
    if (match && (!best || match.index < best.index)) {
      best = { type, index: match.index }
    }
  }
  return best?.type ?? null
}

/**
 * Formats the panel layout as a text description for the system prompt.
 *
 * @param targetPanelType — panel type from classify stage (e.g. "scanner").
 *   When provided, only that panel gets full bounds + elements.
 *   Other panels are listed by name only to save tokens.
 */
export function describePanelLayout(
  panels: PanelInfo[],
  captureWidth: number,
  captureHeight: number,
  mouseX: number,
  mouseY: number,
  targetPanelType?: string | null,
): string {
  if (panels.length === 0) return ''

  const vw = window.innerWidth
  const vh = window.innerHeight

  const focusedPanel = findFocusedPanel(panels, mouseX, mouseY)
  const activePanel = findActivePanel(panels)

  // Determine which panel to show full detail for
  // Priority: classify target > mouse focus > last active
  const targetPanel = targetPanelType
    ? panels.find((p) => p.panelType === targetPanelType)
    : (focusedPanel ?? activePanel)

  // Collect labeled elements
  const labeledElements = collectLabeledElements(panels)

  const lines: string[] = []

  for (const p of panels) {
    const isTarget = targetPanel && p.panelId === targetPanel.panelId
    const sx = Math.round((p.bounds.x / vw) * captureWidth)
    const sy = Math.round((p.bounds.y / vh) * captureHeight)
    const sw = Math.round((p.bounds.width / vw) * captureWidth)
    const sh = Math.round((p.bounds.height / vh) * captureHeight)

    if (isTarget) {
      // Full detail for target panel
      let label = `- **${p.panelType}** panel: region x=${sx}..${sx + sw}, y=${sy}..${sy + sh} **(target)**`

      // Include ALL labeled elements for this panel
      const panelElements = labeledElements.filter((e) => e.panelType === p.panelType)
      for (const el of panelElements) {
        const ex = Math.round(((el.bounds.x + el.bounds.width / 2) / vw) * captureWidth)
        const ey = Math.round(((el.bounds.y + el.bounds.height / 2) / vh) * captureHeight)
        label += `\n  - "${el.label}" at x=${ex}, y=${ey}`
      }

      lines.push(label)
    } else {
      // Minimal: just panel type, no bounds, no elements
      lines.push(`- **${p.panelType}** panel (not the focus)`)
    }
  }

  // Global elements (nav, add-panel, etc.) — always include
  const globalElements = labeledElements.filter((e) => e.panelType === null)
  if (globalElements.length > 0) {
    lines.push('- **global** elements:')
    for (const el of globalElements) {
      const ex = Math.round(((el.bounds.x + el.bounds.width / 2) / vw) * captureWidth)
      const ey = Math.round(((el.bounds.y + el.bounds.height / 2) / vh) * captureHeight)
      lines.push(`  - "${el.label}" at x=${ex}, y=${ey}`)
    }
  }

  const parts = [
    '## panel layout and interactive elements',
    lines.join('\n'),
    '',
    'when pointing at a known element listed above, use its exact coordinates. these are pixel-accurate — do not guess different coordinates for listed elements.',
    '',
    'you may point at ANY element listed in ## interactive elements with the label form [POINT:<element-label>] — it needs no coordinates, including elements of panels not detailed above. when the same label exists in more than one panel, prefer the panel the question is about.',
  ]

  if (targetPanel) {
    parts.push(
      '',
      targetPanelType
        ? `the user is asking about the **${targetPanel.panelType}** panel — its elements are listed with exact coordinates above.`
        : `no panel is named in the question; the **${targetPanel.panelType}** panel currently has focus. if the question is actually about a different panel, use that panel's labels from ## interactive elements instead.`,
    )
  }

  return parts.join('\n')
}
